/**
 * 配置管理数据Hook
 * 职责：管理配置页面的数据加载和状态
 */
import { useState, useEffect, useCallback } from 'react';
import { App } from 'antd';
import { configPresetAPI, type PresetListItem, type WeightSystemConfig } from '../../../services/configPresetAPI';
import { skuAPI } from '../../../services/api';

export const useConfigData = (id: string | undefined) => {
    const { message } = App.useApp();
    const [loading, setLoading] = useState(true);
    const [presets, setPresets] = useState<PresetListItem[]>([]);
    const [currentConfig, setCurrentConfig] = useState<WeightSystemConfig | null>(null);
    const [skuName, setSkuName] = useState<string>('');

    const loadInitialData = useCallback(async () => {
        setLoading(true);
        try {
            // 1. 如果有SKU ID，先加载SKU信息
            if (id) {
                const skuResponse = await skuAPI.get(parseInt(id));
                setSkuName(skuResponse.data.name || '');
            }

            // 2. 加载所有预设
            const presetsData = await configPresetAPI.listPresets();
            setPresets(presetsData.presets);

            // 3. 加载当前SKU的配置（如果存在）
            // TODO: 如果是SKU模式，应该加载SKU特定的配置
            const defaultConfig = await configPresetAPI.getPreset('balanced');
            setCurrentConfig(defaultConfig.config);
        } catch (error) {
            message.error('加载配置失败');
            console.error(error);
        } finally {
            setLoading(false);
        }
    }, [id, message]);

    useEffect(() => {
        loadInitialData();
    }, [loadInitialData]);

    const updateConfig = (metricKey: string, newMetricConfig: any) => {
        if (!currentConfig) return;
        setCurrentConfig({
            ...currentConfig,
            [metricKey]: newMetricConfig,
        });
    };

    const applyPresetToMetric = async (metricKey: string, presetKey: string) => {
        try {
            const presetData = await configPresetAPI.getPreset(presetKey);
            const presetMetricConfig = presetData.config[metricKey as keyof WeightSystemConfig];

            if (!presetMetricConfig) {
                message.warning('预设中不包含该指标配置');
                return;
            }

            updateConfig(metricKey, presetMetricConfig);
            message.success(`已应用 "${presetData.name}" 到当前指标`);
        } catch (error) {
            message.error('应用预设失败');
            console.error(error);
        }
    };

    return {
        loading,
        presets,
        currentConfig,
        skuName,
        updateConfig,
        applyPresetToMetric,
        refresh: loadInitialData
    };
};
